import { type ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface SectionHeaderProps {
  /** Small label above the title. */
  eyebrow?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  align?: 'center' | 'start'
  className?: string
  as?: 'h1' | 'h2'
}

/**
 * The one section-header style used across the whole site, so every
 * section reads the same way: eyebrow → headline → one-line explanation.
 */
export default function SectionHeader({
  eyebrow,
  title,
  subtitle,
  align = 'center',
  className,
  as: Heading = 'h2',
}: SectionHeaderProps) {
  return (
    <div
      className={cn(
        'max-w-3xl space-y-3 sm:space-y-4',
        align === 'center' ? 'mx-auto text-center' : 'text-start',
        className
      )}
    >
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
      <Heading
        className={cn(
          'font-bold leading-[1.16]',
          Heading === 'h1' ? 'text-3xl sm:text-4xl md:text-5xl lg:text-6xl' : 'text-2xl sm:text-3xl md:text-4xl'
        )}
      >
        {title}
      </Heading>
      {subtitle && <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">{subtitle}</p>}
    </div>
  )
}
